import { useState, useMemo } from 'react'
import { createFileRoute, notFound, Link } from '@tanstack/react-router'
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip,
} from 'recharts'
import { metaCourseDescription, formatTermCode } from '@cougargrades/models'
import type { SectionPlus } from '@cougargrades/models/dto'
import { isNullish } from '@cougargrades/utils/nullish'
import { computeCourseAlert, computeInsightSentence, computeTrendDirection } from '@cougargrades/utils/scoring'
import type { CourseAlert } from '@cougargrades/utils/scoring'
import { computeUDI, deriveUDIInputs, computeProfessorUDI, getUDIColor, predictStudentGPA } from '@cougargrades/utils'
import type { UDIResult } from '@cougargrades/utils'
import { courseDataQueryOptions, useCourseData } from '../../lib/services/useCourseData'
import { useSearchSimpleSyllabus } from '../../lib/services/useSearchSimpleSyllabus'
import { GRADE_COLORS } from '../../lib/gradeColors'
// ─── Pure utility types & functions ───────────────────────────────────────────

interface GradeCounts { A: number; B: number; C: number; D: number; F: number; W: number }
type SortKey = 'gpa' | 'sections' | 'passRate' | 'wRate' | 'name'
type SortDir = 'asc' | 'desc'
const GRADE_KEYS = ['A', 'B', 'C', 'D', 'F', 'W'] as const

function aggregateGrades(rows: SectionPlus[]): GradeCounts {
  return rows.reduce(
    (acc, row) => ({
      A: acc.A + (row.A ?? 0), B: acc.B + (row.B ?? 0), C: acc.C + (row.C ?? 0),
      D: acc.D + (row.D ?? 0), F: acc.F + (row.F ?? 0), W: acc.W + (row.W ?? 0),
    }),
    { A: 0, B: 0, C: 0, D: 0, F: 0, W: 0 },
  )
}


function parseGPAFromBadges(badges: { key: string; text: string }[]): number | null {
  const badge = badges.find(b => b.key === 'gpa')
  const parsed = parseFloat(badge?.text ?? '')
  return isNaN(parsed) ? null : parsed
}

function gpaColor(gpa: number | null): string {
  if (gpa == null) return '#94A3B8'
  if (gpa >= 3.5) return '#059669'
  if (gpa >= 2.5) return '#10B981'
  if (gpa >= 1.5) return '#D97706'
  return '#DC2626'
}

function gpaToLetterGrade(gpa: number): string {
  if (gpa >= 3.85) return 'A'; if (gpa >= 3.5) return 'A−'; if (gpa >= 3.15) return 'B+'
  if (gpa >= 2.85) return 'B'; if (gpa >= 2.5) return 'B−'; if (gpa >= 2.15) return 'C+'
  if (gpa >= 1.85) return 'C'; if (gpa >= 1.5) return 'C−'; if (gpa >= 1.15) return 'D+'
  if (gpa >= 0.85) return 'D'; return 'F'
}

// ─── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/c/$courseName')({
  async loader(ctx) {
    const queryOptions = courseDataQueryOptions(ctx.params.courseName)
    const data = await ctx.context.queryClient.ensureQueryData(queryOptions)
    if (isNullish(data)) throw notFound()
    return data
  },
  head: (ctx) => {
    const data = ctx.loaderData
    const metaDescription = metaCourseDescription({ ...(data ?? {}) })
    return {
      meta: [
        { title: `${ctx.params.courseName} Grade Distribution | Unilytics` },
        { name: 'description', content: `${metaDescription}` },
      ],
    }
  },
  component: RouteComponent,
  notFoundComponent: NotFoundComponent,
})

// ─── Main component ───────────────────────────────────────────────────────────

function RouteComponent() {
  const { courseName } = Route.useParams()
  const [profCardSort, setProfCardSort] = useState<'gpa' | 'easy'>('gpa')
  const [profSortKey, setProfSortKey] = useState<SortKey>('gpa')
  const [profSortDir, setProfSortDir] = useState<SortDir>('desc')
  const [sectionsOpen, setSectionsOpen] = useState(false)
  const [historyExpanded, setHistoryExpanded] = useState(false)
  const [studentGPA, setStudentGPA] = useState('')

  // ── Data fetching (unchanged) ──
  const { data, status, isPending } = useCourseData(courseName)
  const { data: sylData } = useSearchSimpleSyllabus(courseName)
  const DID_LOAD = status === 'success' && !isNullish(data)

  // ── Aggregate grades from all sections ──
  const aggregated = useMemo(
    () => DID_LOAD ? aggregateGrades(data.dataGrid.rows) : { A: 0, B: 0, C: 0, D: 0, F: 0, W: 0 },
    [DID_LOAD, data],
  )

  const total = aggregated.A + aggregated.B + aggregated.C + aggregated.D + aggregated.F + aggregated.W
  const avgGPA = DID_LOAD ? parseGPAFromBadges(data.badges) : null
  const wRatePct = total > 0 ? (aggregated.W / total) * 100 : 0
  const fRatePct = total > 0 ? (aggregated.F / total) * 100 : 0
  const passRatePct = total > 0 ? ((aggregated.A + aggregated.B + aggregated.C) / total) * 100 : 0
  const insightSentence = DID_LOAD ? computeInsightSentence(aggregated) : ''
  const udiResult: UDIResult = DID_LOAD
    ? computeUDI(deriveUDIInputs({ avgGPA: avgGPA ?? 0, grades: aggregated, sections: data.sectionCount, schoolId: 'uh' }))
    : computeUDI(deriveUDIInputs({ schoolId: 'uh' }))
  const diffClr = getUDIColor(udiResult.udi)

  // ── Per-term trend (GPA + enrollment) from sections ──
  const trendData = useMemo(() => {
    if (!DID_LOAD) return []
    const byTerm = new Map<number, { pts: number; total: number; enrolled: number }>()
    for (const row of data.dataGrid.rows) {
      const e = byTerm.get(row.term) ?? { pts: 0, total: 0, enrolled: 0 }
      e.pts += row.A * 4 + row.B * 3 + row.C * 2 + row.D * 1
      e.total += row.A + row.B + row.C + row.D + row.F + row.W
      e.enrolled += row.A + row.B + row.C + row.D + row.F + row.W + row.S + row.NCR
      byTerm.set(row.term, e)
    }
    return [...byTerm.entries()]
      .sort(([a], [b]) => a - b)
      .map(([term, e]) => ({
        term: formatTermCode(term),
        gpa: e.total > 0 ? Math.round((e.pts / e.total) * 100) / 100 : null,
        enrolled: e.enrolled,
      }))
  }, [DID_LOAD, data])

  const trendDir = computeTrendDirection(trendData)

  // ── Per-professor data from sections ──
  type ProfEntry = {
    grades: GradeCounts; sectionCount: number; displayName: string; href: string
  }
  const profData = useMemo(() => {
    if (!DID_LOAD) return []
    const byProf = new Map<string, ProfEntry>()
    for (const row of data.dataGrid.rows) {
      const id = row.primaryInstructorName
      const e = byProf.get(id) ?? { grades: { A: 0, B: 0, C: 0, D: 0, F: 0, W: 0 }, sectionCount: 0, displayName: '', href: `/i/${id}` }
      e.grades.A += row.A; e.grades.B += row.B; e.grades.C += row.C
      e.grades.D += row.D; e.grades.F += row.F; e.grades.W += row.W
      e.sectionCount += 1
      if (!e.displayName && row.instructorNames.length > 0) {
        const { firstName, lastName } = row.instructorNames[0]
        e.displayName = `${firstName} ${lastName}`.trim()
      }
      byProf.set(id, e)
    }
    for (const instr of data.relatedInstructors) {
      const e = byProf.get(instr.id)
      if (e) { if (instr.title) e.displayName = instr.title; e.href = instr.href }
    }
    return [...byProf.entries()].map(([id, e]) => {
      const { grades } = e
      const t = grades.A + grades.B + grades.C + grades.D + grades.F + grades.W
      const gpa = t > 0 ? (grades.A * 4 + grades.B * 3 + grades.C * 2 + grades.D * 1) / t : null
      const profWRate = t > 0 ? (grades.W / t) * 100 : 0
      const profUDI = computeProfessorUDI({
        professorAvgGPA: gpa ?? 0,
        courseAvgGPA: avgGPA ?? 0,
        professorWRate: profWRate,
        courseWRate: wRatePct,
        professorSections: e.sectionCount,
        professorGrades: grades,
        schoolId: 'uh',
      })
      return {
        id, name: e.displayName || id, href: e.href, grades,
        gpa, diffScore: profUDI.udi, udiLabel: profUDI.label, graderProfile: profUDI.graderProfile,
        sectionCount: e.sectionCount,
        passRate: t > 0 ? Math.round(((grades.A + grades.B + grades.C) / t) * 100) : 0,
        wRate: t > 0 ? Math.round((grades.W / t) * 100) : 0,
        total: t,
      }
    })
  }, [DID_LOAD, data])

  // ── Active-window professor filtering ──
  // Show profs who taught within last 4 semesters; expand to 8 if fewer than 3 result
  const { activeProfIds, hasHistoricalProfs } = useMemo(() => {
    if (!DID_LOAD || profData.length === 0) return { activeProfIds: new Set<string>(), hasHistoricalProfs: false }
    const profTermsMap = new Map<string, Set<number>>()
    for (const row of data.dataGrid.rows) {
      if (!profTermsMap.has(row.primaryInstructorName)) profTermsMap.set(row.primaryInstructorName, new Set())
      profTermsMap.get(row.primaryInstructorName)!.add(row.term)
    }
    const sortedTerms = [...new Set(data.dataGrid.rows.map(r => r.term))].sort((a, b) => b - a)
    let finalIds = new Set<string>()
    for (const windowSize of [4, 8]) {
      const windowTerms = new Set(sortedTerms.slice(0, windowSize))
      finalIds = new Set(
        profData
          .filter(p => [...(profTermsMap.get(p.id) ?? [])].some(t => windowTerms.has(t)))
          .map(p => p.id),
      )
      if (finalIds.size >= 3 || windowSize === 8) break
    }
    return { activeProfIds: finalIds, hasHistoricalProfs: profData.some(p => !finalIds.has(p.id)) }
  }, [DID_LOAD, data, profData])

  const activeProfData = useMemo(() => profData.filter(p => activeProfIds.has(p.id)), [profData, activeProfIds])
  const historicalProfData = useMemo(() => profData.filter(p => !activeProfIds.has(p.id)), [profData, activeProfIds])

  // ── Seasonal pills ──
  const seasonPills = useMemo(() => {
    if (!DID_LOAD) return []
    const labels: Record<string, string> = { '01': 'Spring', '02': 'Summer', '03': 'Fall' }
    const colors: Record<string, string> = { '01': '#10B981', '02': '#F59E0B', '03': '#6366F1' }
    const order: Record<string, number> = { '03': 0, '01': 1, '02': 2 }
    return Object.entries(data.seasonalAvailability.measured)
      .filter(([, count]) => count > 0)
      .sort(([a], [b]) => (order[a] ?? 9) - (order[b] ?? 9))
      .map(([code]) => ({ label: labels[code] ?? code, color: colors[code] ?? '#94A3B8' }))
  }, [DID_LOAD, data])

  // ── Sorted professor lists ──
  const sortedProfCards = useMemo(() => [...activeProfData].sort((a, b) => {
    if (profCardSort === 'easy') return a.diffScore - b.diffScore
    return (b.gpa ?? 0) - (a.gpa ?? 0)
  }), [activeProfData, profCardSort])

  const profTableSorter = (a: typeof profData[0], b: typeof profData[0]) => {
    if (profSortKey === 'name') return profSortDir === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
    const vals: Record<SortKey, [number, number]> = {
      gpa: [a.gpa ?? -1, b.gpa ?? -1],
      sections: [a.sectionCount, b.sectionCount],
      passRate: [a.passRate, b.passRate],
      wRate: [a.wRate, b.wRate],
      name: [0, 0],
    }
    const [av, bv] = vals[profSortKey]
    return profSortDir === 'desc' ? bv - av : av - bv
  }
  const sortedProfTable = useMemo(() => [...activeProfData].sort(profTableSorter), [activeProfData, profSortKey, profSortDir])
  const sortedHistoricalProfTable = useMemo(() => [...historicalProfData].sort(profTableSorter), [historicalProfData, profSortKey, profSortDir])

  const sortedSections = useMemo(() => DID_LOAD ? [...data.dataGrid.rows].sort((a, b) => b.term - a.term) : [], [DID_LOAD, data])

  const handleProfSort = (key: SortKey) => {
    if (key === profSortKey) setProfSortDir(d => d === 'desc' ? 'asc' : 'desc')
    else { setProfSortKey(key); setProfSortDir('desc') }
  }

  const courseAlert = DID_LOAD && total > 0 ? computeCourseAlert({
    wRate: (aggregated.W / total) * 100,
    fRate: (aggregated.F / total) * 100,
    abRate: ((aggregated.A + aggregated.B) / total) * 100,
    avgGPA: avgGPA ?? 0,
    trend: trendDir,
  }) : null

  const parsedStudentGPA = parseFloat(studentGPA)
  const validStudentGPA = !isNaN(parsedStudentGPA) && parsedStudentGPA >= 0 && parsedStudentGPA <= 4.0
  const gpaPrediction = validStudentGPA && DID_LOAD
    ? predictStudentGPA({ studentGPA: parsedStudentGPA, courseUDI: udiResult })
    : null

  // ── Render ──
  return (
    <div style={{ fontFamily: 'var(--font-family-sans)' }}>
      <style>{`
        @media (max-width: 640px) {
          .course-hero-code { font-size: 28px !important; }
          .course-hero-title { font-size: 22px !important; }
        }
      `}</style>

      {/* ── HERO ── */}
      <div style={{ background: 'var(--school-primary)', backgroundImage: 'linear-gradient(rgba(0,0,0,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.15) 1px, transparent 1px)', backgroundSize: '40px 40px' }}>
        <div className="max-w-6xl mx-auto px-4 py-10">
          <div className="flex flex-col md:flex-row md:items-start gap-8">
            {/* Left */}
            <div className="flex-1 min-w-0">
              <div className="course-hero-code" style={{ fontFamily: 'var(--font-family-mono)', fontSize: 42, fontWeight: 800, color: 'var(--school-secondary)', letterSpacing: '-0.03em', lineHeight: 1, marginBottom: 10 }}>
                {courseName}
              </div>
              <div className="course-hero-title" style={{ fontSize: 32, fontWeight: 700, color: '#fff', marginBottom: 0, lineHeight: 1.25 }}>
                {isPending ? '…' : (data?.meta.description ?? '')}
              </div>
              {DID_LOAD && (
                <>
                  {/* Season pills */}
                  {seasonPills.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {seasonPills.map(s => (
                        <span key={s.label} style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: `${s.color}22`, color: s.color, border: `1px solid ${s.color}44`, letterSpacing: '0.03em' }}>
                          {s.label}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Stat chips */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    <HeroChip>{data.instructorCount} Instructors</HeroChip>
                    <HeroChip>{data.sectionCount} Sections</HeroChip>
                    <HeroChip>{data.firstTaught} – {data.lastTaught}</HeroChip>
                  </div>
                </>
              )}
            </div>

            {/* Right: TCCNS + syllabus badges */}
            {DID_LOAD && (data.tccnsUpdates.length > 0 || (sylData && sylData.items.length > 0)) && (
              <div className="flex-shrink-0 flex flex-col gap-2 md:items-end">
                {data.tccnsUpdates.slice(0, 2).map((u, i) => (
                  <div key={i} style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: '8px 14px', fontSize: 12, color: 'rgba(255,255,255,0.75)', maxWidth: 260 }}>
                    {u.shortMessage}
                  </div>
                ))}
                {sylData && sylData.items.length > 0 && (
                  <div style={{ background: 'rgba(var(--school-primary-rgb),0.12)', border: '1px solid rgba(var(--school-primary-rgb),0.3)', borderRadius: 10, padding: '8px 14px', fontSize: 12, color: 'var(--school-primary)', fontWeight: 600 }}>
                    📄 Syllabus available
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div className="max-w-6xl mx-auto px-4">

        {/* ── COURSE INTELLIGENCE ALERT ── */}
        {courseAlert && <CourseAlertBanner alert={courseAlert} />}

        {/* ── TWO HERO CARDS ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">

          {/* LEFT — UDI */}
          <Card>
            <SectionTitle icon="🔥">Unilytics Difficulty Index</SectionTitle>
            <div className="mt-5" />

            {/* Big score + bar */}
            <div className="flex items-end gap-5 mb-5">
              <div>
                <div style={{ fontFamily: 'var(--font-family-mono)', fontSize: 64, fontWeight: 700, lineHeight: 1, color: diffClr, letterSpacing: '-0.04em' }}>
                  {DID_LOAD ? udiResult.udi.toFixed(1) : '—'}
                </div>
                <div style={{ fontSize: 17, fontWeight: 700, color: diffClr, marginTop: 5 }}>{udiResult.label}</div>
                <div style={{ fontSize: 10, color: '#94A3B8', fontWeight: 600, marginTop: 3, letterSpacing: '0.05em', textTransform: 'uppercase' }}>UDI Score</div>
              </div>
              <div className="flex-1 pb-2">
                <div style={{ position: 'relative', height: 10, borderRadius: 10, background: 'linear-gradient(to right, #10B981, #F59E0B, #EF4444)' }}>
                  {DID_LOAD && (
                    <div style={{ position: 'absolute', top: '50%', left: `${(udiResult.udi / 10) * 100}%`, transform: 'translate(-50%,-50%)', width: 18, height: 18, borderRadius: '50%', background: '#fff', border: `3px solid ${diffClr}`, boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }} />
                  )}
                </div>
                <div className="flex justify-between mt-1" style={{ fontSize: 10, color: '#94A3B8', fontWeight: 600 }}>
                  <span>0 Easy</span><span>10 Very Hard</span>
                </div>
              </div>
            </div>

            {/* Grade distribution bar */}
            <CourseGradeBar grades={aggregated} />
            <div className="mt-4" />

            {/* Insight */}
            {DID_LOAD && (
              <p style={{ fontSize: 13, color: '#475569', fontStyle: 'italic', margin: '0 0 16px', padding: '10px 14px', background: '#F8FAFC', borderRadius: 10, borderLeft: '3px solid var(--school-primary)' }}>
                {insightSentence}
              </p>
            )}

            {/* 3 quick stats */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              <QuickStat value={avgGPA != null ? avgGPA.toFixed(2) : '—'} label="Avg GPA" color={gpaColor(avgGPA)} />
              <QuickStat value={total > 0 ? `${wRatePct.toFixed(1)}%` : '—'} label="W Rate" color={wRatePct > 15 ? '#DC2626' : wRatePct > 8 ? '#D97706' : '#059669'} />
              <QuickStat value={total > 0 ? `${Math.round(passRatePct)}%` : '—'} label="Pass Rate" color={passRatePct > 70 ? '#059669' : passRatePct > 50 ? '#D97706' : '#DC2626'} />
            </div>

            {/* GPA Prediction */}
            <div style={{ borderTop: '1px solid #F1F5F9', paddingTop: 16 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', margin: '0 0 10px' }}>Predict your grade</p>
              <input
                type="number" min={0} max={4} step={0.01}
                value={studentGPA}
                onChange={e => setStudentGPA(e.target.value)}
                placeholder="Your cumulative GPA (e.g. 3.2)"
                style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1px solid #E2E8F0', fontSize: 13, outline: 'none', fontFamily: 'var(--font-family-sans)', boxSizing: 'border-box' }}
              />
              <div className="mt-2">
                {gpaPrediction ? (
                  <div style={{ background: '#F0FDF9', borderRadius: 10, padding: '10px 14px' }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#065F46', margin: 0 }}>
                      Expected: {gpaPrediction.letterGrade} ({gpaPrediction.expectedGPA.toFixed(2)})
                    </p>
                    <p style={{ fontSize: 12, color: '#047857', margin: '3px 0 0' }}>
                      Range: {gpaPrediction.lowGPA.toFixed(2)} – {gpaPrediction.highGPA.toFixed(2)} GPA
                    </p>
                    {gpaPrediction.insight && (
                      <p style={{ fontSize: 11, color: '#64748B', fontStyle: 'italic', margin: '6px 0 0' }}>
                        {gpaPrediction.insight}
                      </p>
                    )}
                  </div>
                ) : studentGPA !== '' && !validStudentGPA ? (
                  <p style={{ fontSize: 12, color: '#DC2626', margin: 0 }}>Enter a GPA between 0.0 and 4.0</p>
                ) : null}
              </div>
              <p style={{ fontSize: 11, color: '#94A3B8', margin: '10px 0 0' }}>⚠ Based on historical data. Not a guarantee.</p>
            </div>
          </Card>

          {/* RIGHT — Professor Intelligence */}
          <Card style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="flex items-center justify-between">
              <SectionTitle icon="👨‍🏫">Choose Your Professor</SectionTitle>
              <select
                value={profCardSort}
                onChange={e => setProfCardSort(e.target.value as typeof profCardSort)}
                style={{ fontSize: 12, padding: '5px 8px', borderRadius: 8, border: '1px solid #E2E8F0', color: '#64748B', cursor: 'pointer', outline: 'none', background: '#fff' }}
              >
                <option value="gpa">Best GPA</option>
                <option value="easy">Easiest</option>
              </select>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', maxHeight: 460, marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {isPending
                ? [1, 2, 3].map(i => <ProfCardSkeleton key={i} />)
                : sortedProfCards.map(prof => <ProfCard key={prof.id} prof={prof} />)
              }
            </div>
            <p style={{ fontSize: 11, color: '#CBD5E1', marginTop: 14, borderTop: '1px solid #F1F5F9', paddingTop: 12 }}>
              Scores based on historical grade data only.
            </p>
          </Card>
        </div>

        {/* ── TREND CHARTS ── */}
        <div className="mt-8">
          <Card>
            <div className="flex items-start justify-between mb-1">
              <SectionTitle icon="📈">How Has This Course Changed Over Time?</SectionTitle>
              {trendDir !== 'stable' && (
                <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20, color: trendDir === 'easier' ? '#059669' : '#DC2626', background: trendDir === 'easier' ? 'rgba(5,150,105,0.1)' : 'rgba(220,38,38,0.1)', whiteSpace: 'nowrap', marginLeft: 12 }}>
                  {trendDir === 'easier' ? '↑ Trending easier' : '↓ Trending harder'}
                </span>
              )}
            </div>
            <p style={{ fontSize: 13, color: '#94A3B8', margin: '6px 0 20px' }}>Average GPA and enrollment by semester across all instructors</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Average GPA</p>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={trendData} margin={{ top: 4, right: 8, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="term" tick={{ fontSize: 10, fill: '#94A3B8' }} angle={-45} textAnchor="end" interval="preserveStartEnd" />
                    <YAxis domain={[0, 4]} tick={{ fontSize: 11, fill: '#94A3B8' }} tickFormatter={(v: number) => v.toFixed(1)} width={32} />
                    <ReTooltip formatter={(v) => [typeof v === 'number' ? v.toFixed(2) : '—', 'Avg GPA']} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E2E8F0' }} />
                    <Line type="monotone" dataKey="gpa" stroke="var(--school-primary)" strokeWidth={2.5} dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Enrollment</p>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={trendData} margin={{ top: 4, right: 8, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="term" tick={{ fontSize: 10, fill: '#94A3B8' }} angle={-45} textAnchor="end" interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} width={40} />
                    <ReTooltip formatter={(v) => [v, 'Students enrolled']} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E2E8F0' }} />
                    <Line type="monotone" dataKey="enrolled" stroke="#0D1B2A" strokeWidth={2.5} dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Card>
        </div>

        {/* ── PROFESSOR TABLE ── */}
        {DID_LOAD && profData.length > 0 && (
          <div className="mt-8">
            <Card>
              <div className="flex items-center gap-3 flex-wrap">
                <SectionTitle icon="📊">Professor Comparison</SectionTitle>
                {hasHistoricalProfs && (
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: 'rgba(148,163,184,0.12)', color: '#94A3B8', border: '1px solid rgba(148,163,184,0.2)', letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>
                    Showing recent instructors
                  </span>
                )}
              </div>
              <p style={{ fontSize: 13, color: '#94A3B8', margin: '6px 0 20px' }}>Click any professor to view their full profile</p>
              <div className="overflow-x-auto">
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #F1F5F9' }}>
                      <SortTh label="Professor" sortKey="name" current={profSortKey} dir={profSortDir} onSort={handleProfSort} align="left" />
                      <SortTh label="Avg GPA" sortKey="gpa" current={profSortKey} dir={profSortDir} onSort={handleProfSort} />
                      <th style={thStyle}>Difficulty</th>
                      <SortTh label="Pass Rate" sortKey="passRate" current={profSortKey} dir={profSortDir} onSort={handleProfSort} className="hidden sm:table-cell" />
                      <SortTh label="W Rate" sortKey="wRate" current={profSortKey} dir={profSortDir} onSort={handleProfSort} className="hidden sm:table-cell" />
                      <SortTh label="Sections" sortKey="sections" current={profSortKey} dir={profSortDir} onSort={handleProfSort} className="hidden sm:table-cell" />
                      <th style={thStyle} />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedProfTable.map(prof => (
                      <ProfTableRow key={prof.id} prof={prof} isTop={false} />
                    ))}
                    {hasHistoricalProfs && !historyExpanded && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0 }}>
                          <button
                            onClick={() => setHistoryExpanded(true)}
                            style={{ width: '100%', padding: '12px 16px', background: '#F8FAFC', border: 'none', borderTop: '1px dashed #E2E8F0', cursor: 'pointer', fontSize: 13, color: '#94A3B8', fontWeight: 600, textAlign: 'center', fontFamily: 'var(--font-family-sans)' }}
                            onMouseEnter={e => { e.currentTarget.style.color = '#64748B'; e.currentTarget.style.background = '#F1F5F9' }}
                            onMouseLeave={e => { e.currentTarget.style.color = '#94A3B8'; e.currentTarget.style.background = '#F8FAFC' }}
                          >
                            Show {historicalProfData.length} more professor{historicalProfData.length !== 1 ? 's' : ''} from earlier semesters ↓
                          </button>
                        </td>
                      </tr>
                    )}
                    {hasHistoricalProfs && historyExpanded && (
                      <>
                        <tr>
                          <td colSpan={7} style={{ padding: '8px 16px', background: '#F8FAFC', borderTop: '1px dashed #E2E8F0' }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Earlier semesters</span>
                          </td>
                        </tr>
                        {sortedHistoricalProfTable.map(prof => (
                          <ProfTableRow key={prof.id} prof={prof} isTop={false} muted />
                        ))}
                        <tr>
                          <td colSpan={7} style={{ padding: 0 }}>
                            <button
                              onClick={() => setHistoryExpanded(false)}
                              style={{ width: '100%', padding: '10px 16px', background: '#F8FAFC', border: 'none', borderTop: '1px dashed #E2E8F0', cursor: 'pointer', fontSize: 12, color: '#94A3B8', fontWeight: 600, textAlign: 'center', fontFamily: 'var(--font-family-sans)' }}
                              onMouseEnter={e => { e.currentTarget.style.color = '#64748B' }}
                              onMouseLeave={e => { e.currentTarget.style.color = '#94A3B8' }}
                            >
                              ↑ Collapse
                            </button>
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* ── PAST SECTIONS (collapsible) ── */}
        <div className="mt-8 mb-16">
          <div style={{ background: '#F8FAFC', borderRadius: 16, border: '1px solid rgba(13,27,42,0.06)' }}>
            <button
              onClick={() => setSectionsOpen(v => !v)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-family-sans)' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#374151' }}>Past Sections</span>
                <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'rgba(13,27,42,0.07)', color: '#64748B' }}>
                  {DID_LOAD ? data.sectionCount : '…'}
                </span>
              </div>
              <span style={{ fontSize: 14, color: '#94A3B8', display: 'inline-block', transform: sectionsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</span>
            </button>

            {sectionsOpen && (
              <div style={{ overflowX: 'auto', padding: '0 8px 16px' }}>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                      {['Term', 'Professor', 'Sec', 'Enrolled', 'A', 'B', 'C', 'D', 'F', 'W', 'GPA'].map(h => (
                        <th
                          key={h}
                          className={['Sec', 'Enrolled', 'A', 'B', 'C', 'D', 'F', 'W'].includes(h) ? 'hidden sm:table-cell' : ''}
                          style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: ['Term', 'Professor'].includes(h) ? 'left' : 'center' }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedSections.map((row, i) => {
                      const instrDisplay = row.instructorNames.length > 0
                        ? `${row.instructorNames[0].firstName} ${row.instructorNames[0].lastName}`.trim()
                        : row.primaryInstructorName
                      const secGpaClr = gpaColor(row.semesterGPA)
                      const secTotal = row.A + row.B + row.C + row.D + row.F + row.W
                      return (
                        <tr key={row._id} style={{ borderBottom: '1px solid #F1F5F9', background: i % 2 === 0 ? 'transparent' : 'rgba(13,27,42,0.01)' }}>
                          <td style={{ padding: '8px 10px', color: '#374151', whiteSpace: 'nowrap' }}>{formatTermCode(row.term)}</td>
                          <td style={{ padding: '8px 10px', maxWidth: 180 }}>
                            <Link
                              to="/i/$instructorName"
                              params={{ instructorName: row.primaryInstructorName }}
                              style={{ color: '#0D1B2A', textDecoration: 'none', fontWeight: 500 }}
                              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'var(--school-primary)' }}
                              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#0D1B2A' }}
                            >
                              {instrDisplay}
                            </Link>
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'center', color: '#94A3B8' }} className="hidden sm:table-cell">{row.sectionNumber}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'center', color: '#64748B' }} className="hidden sm:table-cell">
                            {row.totalEnrolled ?? (secTotal > 0 ? secTotal : '—')}
                          </td>
                          {GRADE_KEYS.map(g => (
                            <td key={g} style={{ padding: '8px 10px', textAlign: 'center', fontFamily: 'var(--font-family-mono)', fontWeight: row[g] > 0 ? 600 : 400, color: row[g] > 0 ? GRADE_COLORS[g] : '#E2E8F0' }} className="hidden sm:table-cell">
                              {row[g] > 0 ? row[g] : '—'}
                            </td>
                          ))}
                          <td style={{ padding: '8px 10px', textAlign: 'center', fontFamily: 'var(--font-family-mono)', fontWeight: 600, color: secGpaClr }}>
                            {row.semesterGPA != null ? row.semesterGPA.toFixed(2) : '—'}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const thStyle: React.CSSProperties = {
  padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#94A3B8',
  textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center',
}

const ALERT_STYLES: Record<CourseAlert['severity'], { bg: string; border: string; icon: string; label: string; labelColor: string; textColor: string }> = {
  danger:   { bg: 'rgba(220,38,38,0.06)',    border: 'rgba(220,38,38,0.25)',    icon: '⚠️', label: 'HEADS UP',      labelColor: '#DC2626', textColor: '#7F1D1D' },
  warning:  { bg: 'rgba(217,119,6,0.06)',    border: 'rgba(217,119,6,0.25)',    icon: '📢', label: 'WORTH KNOWING', labelColor: '#D97706', textColor: '#78350F' },
  positive: { bg: 'rgba(5,150,105,0.06)',    border: 'rgba(5,150,105,0.25)',    icon: '✅', label: 'GOOD NEWS',     labelColor: '#059669', textColor: '#064E3B' },
  info:     { bg: 'rgba(var(--school-primary-rgb),0.06)',    border: 'rgba(var(--school-primary-rgb),0.25)',    icon: 'ℹ️', label: 'FYI',           labelColor: '#0891B2', textColor: '#164E63' },
}

function CourseAlertBanner({ alert }: { alert: CourseAlert }) {
  const s = ALERT_STYLES[alert.severity]
  return (
    <div style={{ marginTop: 24, borderLeft: `4px solid ${s.border}`, background: s.bg, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderRadius: '0 10px 10px 0' }}>
      <span style={{ fontSize: 20, flexShrink: 0, lineHeight: 1 }}>{s.icon}</span>
      <p style={{ fontSize: 14, fontWeight: 600, color: s.textColor, margin: 0, lineHeight: 1.5 }}>
        {alert.message}
      </p>
    </div>
  )
}

function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(13,27,42,0.07)', boxShadow: '0 2px 8px rgba(13,27,42,0.05)', padding: 24, ...style }}>
      {children}
    </div>
  )
}

function SectionTitle({ children, icon }: { children: React.ReactNode; icon?: string }) {
  return (
    <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0D1B2A', display: 'flex', alignItems: 'center', gap: 8, borderLeft: '3px solid var(--school-primary)', paddingLeft: 12, margin: 0 }}>
      {icon && <span>{icon}</span>}
      {children}
    </h2>
  )
}

function HeroChip({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ fontSize: 12, padding: '4px 11px', borderRadius: 20, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>
      {children}
    </span>
  )
}

function QuickStat({ value, label, color }: { value: string; label: string; color: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '12px 8px', background: '#F8FAFC', borderRadius: 12 }}>
      <div style={{ fontFamily: 'var(--font-family-mono)', fontSize: 20, fontWeight: 600, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', marginTop: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
    </div>
  )
}

function CourseGradeBar({ grades }: { grades: GradeCounts }) {
  const total = GRADE_KEYS.reduce((s, k) => s + grades[k], 0)
  if (total === 0) return <div style={{ height: 14, background: '#F1F5F9', borderRadius: 10 }} />
  const segments = GRADE_KEYS
    .map(g => ({ g, pct: (grades[g] / total) * 100, count: grades[g] }))
    .filter(s => s.pct >= 0.5)
  return (
    <div>
      <div style={{ display: 'flex', width: '100%', gap: 2, height: 16, borderRadius: 10, overflow: 'hidden' }}>
        {segments.map(({ g, pct, count }, i) => {
          const r = 10
          const first = i === 0, last = i === segments.length - 1
          return (
            <div
              key={g}
              title={`${g}: ${pct.toFixed(1)}% (${count.toLocaleString()} students)`}
              style={{
                width: `${pct}%`,
                backgroundColor: GRADE_COLORS[g],
                borderRadius: first && last ? r : first ? `${r}px 2px 2px ${r}px` : last ? `2px ${r}px ${r}px 2px` : 2,
                minWidth: pct > 1 ? 4 : 0,
              }}
            />
          )
        })}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', marginTop: 8 }}>
        {segments.map(({ g, pct }) => (
          <span key={g} style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, color: '#64748B' }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: GRADE_COLORS[g], display: 'inline-block', flexShrink: 0 }} />
            <span style={{ fontFamily: 'var(--font-family-mono)', color: '#374151', fontWeight: 600 }}>{g}</span>
            {pct.toFixed(0)}%
          </span>
        ))}
      </div>
    </div>
  )
}

type ProfDataItem = ReturnType<typeof buildProfDataItem>
function buildProfDataItem(id: string, e: { grades: GradeCounts; sectionCount: number; displayName: string; href: string }, courseAvgGPA = 0, courseWRate = 0) {
  const { grades } = e
  const t = grades.A + grades.B + grades.C + grades.D + grades.F + grades.W
  const gpa = t > 0 ? (grades.A * 4 + grades.B * 3 + grades.C * 2 + grades.D * 1) / t : null
  const profWRate = t > 0 ? (grades.W / t) * 100 : 0
  const profUDI = computeProfessorUDI({
    professorAvgGPA: gpa ?? 0,
    courseAvgGPA,
    professorWRate: profWRate,
    courseWRate,
    professorSections: e.sectionCount,
    professorGrades: grades,
    schoolId: 'uh',
  })
  return {
    id, name: e.displayName || id, href: e.href, grades, gpa,
    diffScore: profUDI.udi, udiLabel: profUDI.label, graderProfile: profUDI.graderProfile,
    sectionCount: e.sectionCount,
    passRate: t > 0 ? Math.round(((grades.A + grades.B + grades.C) / t) * 100) : 0,
    wRate: t > 0 ? Math.round((grades.W / t) * 100) : 0,
    total: t,
  }
}
// (profData items match this shape)

function ProfCard({ prof }: { prof: { id: string; name: string; href: string; grades: GradeCounts; gpa: number | null; diffScore: number; udiLabel: string; graderProfile: string; sectionCount: number; total: number } }) {
  const gColor = gpaColor(prof.gpa)
  return (
    <div style={{ border: '1px solid #F1F5F9', borderRadius: 12, padding: '12px 14px', background: '#FAFAFA', flexShrink: 0 }}>
      <div style={{ marginBottom: 8 }}>
        <Link
          to="/i/$instructorName"
          params={{ instructorName: prof.id }}
          style={{ fontSize: 14, fontWeight: 700, color: '#0D1B2A', textDecoration: 'none' }}
          onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'var(--school-primary)' }}
          onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#0D1B2A' }}
        >
          {prof.name}
        </Link>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 11, fontWeight: 700, color: gColor, background: `${gColor}18`, padding: '2px 7px', borderRadius: 6 }}>
          {prof.gpa != null ? prof.gpa.toFixed(2) : '—'} GPA
        </span>
        <span style={{ fontSize: 11, color: '#94A3B8' }}>{prof.sectionCount} sections</span>
      </div>
      {/* Mini grade bar */}
      {prof.total > 0 && (
        <div style={{ display: 'flex', width: '100%', gap: 1, height: 5, borderRadius: 4, overflow: 'hidden', marginBottom: 6 }}>
          {GRADE_KEYS
            .map(g => ({ g, pct: (prof.grades[g] / prof.total) * 100 }))
            .filter(s => s.pct >= 0.5)
            .map(({ g, pct }) => (
              <div key={g} title={`${g}: ${pct.toFixed(1)}%`} style={{ width: `${pct}%`, backgroundColor: GRADE_COLORS[g], minWidth: 2 }} />
            ))}
        </div>
      )}
    </div>
  )
}

function ProfCardSkeleton() {
  return (
    <div style={{ border: '1px solid #F1F5F9', borderRadius: 12, padding: '12px 14px', background: '#FAFAFA' }}>
      <div className="animate-pulse">
        <div style={{ height: 14, background: '#E2E8F0', borderRadius: 6, width: '60%', marginBottom: 8 }} />
        <div style={{ height: 11, background: '#E2E8F0', borderRadius: 6, width: '40%', marginBottom: 8 }} />
        <div style={{ height: 5, background: '#E2E8F0', borderRadius: 4 }} />
      </div>
    </div>
  )
}

type ProfRow = { id: string; name: string; gpa: number | null; diffScore: number; passRate: number; wRate: number; sectionCount: number }
function ProfTableRow({ prof, isTop, muted = false }: { prof: ProfRow; isTop: boolean; muted?: boolean }) {
  return (
    <tr
      style={{
        borderBottom: '1px solid #F8FAFC',
        borderLeft: isTop ? '4px solid var(--school-primary)' : '4px solid transparent',
        background: isTop ? 'rgba(var(--school-primary-rgb),0.03)' : muted ? 'rgba(241,245,249,0.5)' : 'transparent',
        opacity: muted ? 0.75 : 1,
      }}
    >
      <td style={{ padding: '11px 12px', fontWeight: 600 }}>
        <Link
          to="/i/$instructorName"
          params={{ instructorName: prof.id }}
          style={{ color: '#0D1B2A', textDecoration: 'none' }}
          onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'var(--school-primary)' }}
          onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#0D1B2A' }}
        >
          {isTop && <span style={{ fontSize: 11, marginRight: 6, color: 'var(--school-primary)' }}>⭐</span>}
          {prof.name}
        </Link>
      </td>
      <td style={{ padding: '11px 12px', textAlign: 'center', fontFamily: 'var(--font-family-mono)', fontWeight: 600, color: gpaColor(prof.gpa) }}>
        {prof.gpa != null ? prof.gpa.toFixed(2) : '—'}
      </td>
      <td style={{ padding: '11px 12px', textAlign: 'center', fontFamily: 'var(--font-family-mono)', fontWeight: 700, color: getUDIColor(prof.diffScore) }}>
        {prof.diffScore.toFixed(1)}
      </td>
      <td style={{ padding: '11px 12px', textAlign: 'center', color: '#64748B' }} className="hidden sm:table-cell">{prof.passRate}%</td>
      <td style={{ padding: '11px 12px', textAlign: 'center', color: '#64748B' }} className="hidden sm:table-cell">{prof.wRate}%</td>
      <td style={{ padding: '11px 12px', textAlign: 'center', color: '#64748B' }} className="hidden sm:table-cell">{prof.sectionCount}</td>
      <td style={{ padding: '11px 8px', textAlign: 'center' }}>
        <Link to="/i/$instructorName" params={{ instructorName: prof.id }} style={{ fontSize: 13, color: 'var(--school-primary)', textDecoration: 'none', fontWeight: 700 }}>→</Link>
      </td>
    </tr>
  )
}

function SortTh({ label, sortKey, current, dir, onSort, align, className }: {
  label: string; sortKey: SortKey; current: SortKey; dir: SortDir
  onSort: (k: SortKey) => void; align?: 'left' | 'center'; className?: string
}) {
  const active = current === sortKey
  return (
    <th
      className={className}
      onClick={() => onSort(sortKey)}
      style={{ padding: '10px 12px', fontSize: 11, fontWeight: 700, color: active ? '#0D1B2A' : '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: align ?? 'center', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
    >
      {label}{active ? (dir === 'desc' ? ' ↓' : ' ↑') : ''}
    </th>
  )
}

// ─── Not found ────────────────────────────────────────────────────────────────

function NotFoundComponent() {
  const { courseName } = Route.useParams()
  return (
    <div style={{ maxWidth: 480, margin: '80px auto', padding: '0 24px', textAlign: 'center', fontFamily: 'var(--font-family-sans)' }}>
      <p style={{ fontSize: 48, margin: '0 0 16px' }}>🔍</p>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0D1B2A', margin: '0 0 8px' }}>Course Not Found</h1>
      <p style={{ color: '#64748B', margin: '0 0 20px' }}>
        Course "<code style={{ fontFamily: 'var(--font-family-mono)', color: 'var(--school-primary)' }}>{courseName}</code>" could not be found.
      </p>
      <Link to="/g/$groupId" params={{ groupId: '10' }} style={{ color: 'var(--school-primary)', textDecoration: 'none', fontWeight: 600 }}>
        ← Browse all courses
      </Link>
    </div>
  )
}
